// =============================================================================
// Origin UI — src/App.tsx
// Root application component.
// Wraps the app in the MSAL authentication provider and React Router.
// =============================================================================

import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { RouterProvider } from 'react-router-dom';
import { msalConfig } from '@/config/authConfig';
import { initApiService } from '@/services/apiService';
import { router } from '@/config/routes';

// Create the MSAL instance (singleton for the app lifecycle)
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize the API service with the MSAL instance for token acquisition
initApiService(msalInstance);

// Restore the active account on startup. Only fall back to accounts[0]
// if MSAL has no persisted active account — avoids overriding the correct
// @omantrading.com account with a stale @oq.com token from the browser.
if (!msalInstance.getActiveAccount()) {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    // If there's an @omantrading.com account cached, prefer it
    const omantradingAccount = accounts.find((a) =>
      a.username?.toLowerCase().endsWith('@omantrading.com')
    );
    msalInstance.setActiveAccount(omantradingAccount ?? accounts[0]);
  }
}

// Listen for login success to set the active account
msalInstance.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS &&
    event.payload &&
    'account' in event.payload &&
    event.payload.account
  ) {
    msalInstance.setActiveAccount(event.payload.account);
  }
});

/**
 * Root App component. Provides authentication context to the entire app.
 */
export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <RouterProvider router={router} />
    </MsalProvider>
  );
}
